<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'ClientWixtarDispatchList' }" class="btn btn-secondary">
          <i class="ri-history-line"></i>
          派券紀錄
        </router-link>
        <button class="btn btn-secondary" :disabled="loading" @click="handleRefresh">
          <i class="ri-refresh-line"></i>
          重新整理
        </button>
      </div>
    </div>

    <div class="page-container">
      <div class="tab-bar">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'catalog' }"
          @click="activeTab = 'catalog'"
        >
          <i class="ri-ticket-2-line"></i>
          票券目錄
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'member' }"
          @click="activeTab = 'member'"
        >
          <i class="ri-user-search-line"></i>
          會員持券查詢
        </button>
      </div>

      <!-- ============ 票券目錄 ============ -->
      <template v-if="activeTab === 'catalog'">
        <div class="page-toolbar">
          <div class="filter-group search-box">
            <input
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="搜尋票券名稱、票券 ID、活動名稱..."
              @input="handleSearch"
            />
            <i class="ri-search-line"></i>
          </div>
          <div class="filter-group">
            <label class="filter-label">票券活動</label>
            <select v-model="filterActivityId" class="form-control" @change="handleFilterChange">
              <option value="">全部活動</option>
              <option
                v-for="activity in activities"
                :key="activity.activityId"
                :value="activity.activityId"
              >
                {{ activity.activitySubject || `活動 ${activity.activityId}` }}
              </option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">狀態</label>
            <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
              <option value="">全部狀態</option>
              <option value="available">可派發</option>
              <option value="sold_out">已發完</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">排序</label>
            <select v-model="sortBy" class="form-control" @change="handleFilterChange">
              <option value="activity">依活動</option>
              <option value="name">依票券名稱</option>
              <option value="available">依剩餘數量</option>
            </select>
          </div>
        </div>

        <p v-if="fetchedAt && !loading" class="data-time-hint">
          <i class="ri-time-line"></i>
          資料取自 Wixtar 於 {{ formatDateTime(fetchedAt) }}，剩餘數量為當下快照，實際派券時會再次向 Wixtar 確認。
        </p>

        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>載入中...</p>
        </div>

        <div v-else-if="configError" class="config-error-state">
          <i class="ri-plug-line"></i>
          <p>{{ error }}</p>
          <router-link :to="{ name: 'ClientSystemSettings', query: { tab: 'wixtar' } }" class="btn btn-primary">
            前往 Wixtar 整合設定
          </router-link>
        </div>

        <div v-else-if="error" class="error-state">
          <p class="error-message">{{ error }}</p>
          <button class="btn btn-secondary" @click="fetchCatalog">重試</button>
        </div>

        <div v-else class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>票券名稱</th>
                <th>所屬活動</th>
                <th>票券 ID</th>
                <th>總量</th>
                <th>剩餘</th>
                <th>狀態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="tickets.length === 0">
                <td colspan="7" class="empty-state">
                  Wixtar 沒有可用的票券，或不符合目前的篩選條件
                </td>
              </tr>
              <tr v-for="ticket in tickets" :key="ticket.ticketId">
                <td class="cell-ticket-name">
                  <strong>{{ ticket.ticketName }}</strong>
                  <div v-if="ticket.ticketDescription" class="cell-sub">{{ ticket.ticketDescription }}</div>
                </td>
                <td>{{ ticket.activitySubject || '-' }}</td>
                <td class="cell-code">{{ ticket.ticketId }}</td>
                <td>{{ formatQuantity(ticket.quantity) }}</td>
                <td>
                  <strong :class="{ 'text-danger': ticket.availableQuantity === 0 }">
                    {{ formatQuantity(ticket.availableQuantity) }}
                  </strong>
                </td>
                <td>
                  <span :class="statusBadgeClass(ticket.status)">{{ statusText(ticket.status) }}</span>
                </td>
                <td>
                  <div class="action-buttons">
                    <router-link
                      :to="{ name: 'ClientWixtarTicketDetail', params: { ticketId: ticket.ticketId } }"
                      class="btn btn-sm btn-secondary"
                    >
                      <i class="ri-file-list-line"></i>
                      詳情
                    </router-link>
                    <router-link
                      v-if="ticket.status !== 'sold_out'"
                      :to="{ name: 'ClientWixtarTicketDispatch', params: { ticketId: ticket.ticketId } }"
                      class="btn btn-sm btn-primary"
                    >
                      <i class="ri-send-plane-line"></i>
                      派券
                    </router-link>
                    <button v-else class="btn btn-sm btn-primary" disabled>
                      <i class="ri-send-plane-line"></i>
                      派券
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <ListPagination
            v-if="pagination.totalPages > 1"
            :page="currentPage"
            :total-pages="pagination.totalPages"
            :total="pagination.totalItems"
            @change="goToPage"
          />
        </div>
      </template>

      <!-- ============ 會員持券查詢 ============ -->
      <template v-else>
        <div class="member-query-panel">
          <MemberSearch
            v-model="queryMembers"
            :max-selections="1"
            placeholder="輸入會員名稱、電話或 Email，查詢其 Wixtar 票券..."
            @selected="handleMemberSelected"
            @cleared="handleMemberCleared"
          />
        </div>

        <div v-if="memberLoading" class="loading-state">
          <div class="spinner"></div>
          <p>查詢 Wixtar 票券中...</p>
        </div>

        <div v-else-if="memberError" class="error-state">
          <p class="error-message">{{ memberError }}</p>
          <button v-if="queryMembers.length" class="btn btn-secondary" @click="fetchMemberTickets">重試</button>
        </div>

        <div v-else-if="!queryMembers.length" class="empty-hint">
          <i class="ri-user-search-line"></i>
          <p>請先搜尋並選擇一位會員</p>
        </div>

        <div v-else class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>票券名稱</th>
                <th>票券 ID</th>
                <th>狀態</th>
                <th>有效期限</th>
                <th>原始資料</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="memberTickets.length === 0">
                <td colspan="5" class="empty-state">
                  此會員在 Wixtar 沒有可用的票券
                </td>
              </tr>
              <template v-for="(item, index) in memberTickets" :key="`${item.ticketId}-${index}`">
                <tr>
                  <td>{{ item.ticketName }}</td>
                  <td class="cell-code">{{ item.ticketId }}</td>
                  <td>{{ item.status || '-' }}</td>
                  <td>{{ item.validTo || '-' }}</td>
                  <td>
                    <button class="btn btn-sm btn-secondary" @click="toggleRaw(index)">
                      <i :class="expandedRawIndex === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                      {{ expandedRawIndex === index ? '收起' : '展開' }}
                    </button>
                  </td>
                </tr>
                <tr v-if="expandedRawIndex === index">
                  <td colspan="5">
                    <pre class="raw-json">{{ JSON.stringify(item.raw, null, 2) }}</pre>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </template>
    </div>

  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import { showError } from '@/utils/swal.js';
import MemberSearch from '@/components/MemberSearch.vue';

/** Wixtar 未設定或未啟用時，導引使用者去設定頁而不是顯示紅字錯誤 */
const CONFIG_ERROR_CODES = ['WIXTAR_CONFIG_NOT_FOUND', 'WIXTAR_CONFIG_INACTIVE'];

export default {
  name: 'WixtarTicketList',
  components: { MemberSearch },
  setup() {
    const { pageTitle } = usePageTitle('Wixtar 票券管理');

    const activeTab = ref('catalog');

    // 票券目錄
    const tickets = ref([]);
    const activities = ref([]);
    const fetchedAt = ref('');
    const loading = ref(false);
    const error = ref(null);
    const configError = ref(false);
    const searchQuery = ref('');
    const filterActivityId = ref('');
    const filterStatus = ref('');
    const sortBy = ref('activity');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const pagination = reactive({ totalItems: 0, totalPages: 1 });
    const searchTimer = ref(null);

    // 會員持券查詢
    const queryMembers = ref([]);
    const memberTickets = ref([]);
    const memberLoading = ref(false);
    const memberError = ref(null);
    const expandedRawIndex = ref(null);

    const resolveErrorCode = (err) => err?.code || err?.response?.data?.error?.code || null;

    const fetchCatalog = async (refresh = false) => {
      loading.value = true;
      error.value = null;
      configError.value = false;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
          sortBy: sortBy.value
        };
        if (searchQuery.value) params.search = searchQuery.value;
        if (filterActivityId.value) params.activityId = filterActivityId.value;
        if (filterStatus.value) params.status = filterStatus.value;
        if (refresh) params.refresh = true;

        const response = await wixtarService.getTicketCatalog(params);

        if (response && response.success && response.data) {
          tickets.value = response.data.items || [];
          activities.value = response.data.activities || [];
          fetchedAt.value = response.data.fetchedAt || '';
          pagination.totalItems = response.data.pagination?.totalItems || 0;
          pagination.totalPages = response.data.pagination?.totalPages || 1;
        } else {
          error.value = response?.message || '取得 Wixtar 票券目錄失敗';
        }
      } catch (err) {
        console.error('取得 Wixtar 票券目錄失敗:', err);
        error.value = err.message || '取得 Wixtar 票券目錄失敗';
        configError.value = CONFIG_ERROR_CODES.includes(resolveErrorCode(err));
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimer.value) clearTimeout(searchTimer.value);
      searchTimer.value = setTimeout(() => {
        currentPage.value = 1;
        fetchCatalog();
      }, 400);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchCatalog();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= pagination.totalPages) {
        currentPage.value = page;
        fetchCatalog();
      }
    };

    const handleRefresh = () => {
      if (activeTab.value === 'catalog') {
        // 強制略過後端快取，取得 Wixtar 最新剩餘數量
        fetchCatalog(true);
      } else if (queryMembers.value.length) {
        fetchMemberTickets();
      }
    };

    const formatQuantity = (value) => (value === null || value === undefined ? '未提供' : value);

    const statusText = (status) => {
      if (status === 'available') return '可派發';
      if (status === 'sold_out') return '已發完';
      return '數量未提供';
    };

    const statusBadgeClass = (status) => {
      if (status === 'available') return 'badge badge-success';
      if (status === 'sold_out') return 'badge badge-secondary';
      return 'badge badge-warning';
    };

    const formatDateTime = (value) => {
      if (!value) return '-';
      return new Date(value).toLocaleString('zh-TW');
    };

    // Wixtar 會員票券的欄位名稱會隨版本變動，逐一提供備援欄位
    const normalizeMemberTicket = (raw) => ({
      ticketId: String(raw?.id ?? raw?.ticket_id ?? raw?.ticket_no ?? '-'),
      ticketName: raw?.name || raw?.ticket_name || raw?.subject || '（未提供名稱）',
      status: raw?.status || raw?.state || raw?.use_status || null,
      validTo: raw?.end_date || raw?.expiry_date || raw?.valid_to || raw?.end_time || null,
      raw
    });

    const extractMemberTickets = (data) => {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.tickets)) return data.tickets;
      if (Array.isArray(data?.ticket)) return data.ticket;
      if (Array.isArray(data?.data)) return data.data;
      return [];
    };

    const fetchMemberTickets = async () => {
      const member = queryMembers.value[0];
      if (!member) return;

      memberLoading.value = true;
      memberError.value = null;
      expandedRawIndex.value = null;

      try {
        // 既有 API 回傳的是 Wixtar 原始 payload（已去除標準包裝）
        const data = await wixtarService.getMemberTickets(member.id, {
          identifierType: 'crm_member_id'
        });
        memberTickets.value = extractMemberTickets(data).map(normalizeMemberTicket);
      } catch (err) {
        console.error('取得會員 Wixtar 票券失敗:', err);
        memberError.value = err.message
          || '取得會員 Wixtar 票券失敗，請確認此會員的手機號碼已在 Wixtar 註冊';
      } finally {
        memberLoading.value = false;
      }
    };

    const handleMemberSelected = (member) => {
      if (member) fetchMemberTickets();
    };

    const handleMemberCleared = () => {
      memberTickets.value = [];
      memberError.value = null;
      expandedRawIndex.value = null;
    };

    const toggleRaw = (index) => {
      expandedRawIndex.value = expandedRawIndex.value === index ? null : index;
    };

    onMounted(() => {
      fetchCatalog();
    });

    return {
      pageTitle,
      activeTab,
      tickets,
      activities,
      fetchedAt,
      loading,
      error,
      configError,
      searchQuery,
      filterActivityId,
      filterStatus,
      sortBy,
      currentPage,
      pagination,
      fetchCatalog,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleRefresh,
      formatQuantity,
      statusText,
      statusBadgeClass,
      formatDateTime,
      queryMembers,
      memberTickets,
      memberLoading,
      memberError,
      expandedRawIndex,
      fetchMemberTickets,
      handleMemberSelected,
      handleMemberCleared,
      toggleRaw
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* 7 個欄位，手機版需可橫向捲動 */
.table-wrapper table {
  min-width: 900px;
}

.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 16px;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 14px;
  color: #6c757d;
  cursor: pointer;
}

.tab-btn.active {
  color: #1890ff;
  border-bottom-color: #1890ff;
  font-weight: 600;
}

.data-time-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6c757d;
  margin: 0 0 12px;
}

thead {
  background-color: #f5f5f5;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

th {
  font-weight: 600;
  color: #333;
}

tbody tr:hover {
  background-color: #f9f9f9;
}

.cell-ticket-name {
  min-width: 220px;
}

.cell-sub {
  font-size: 12px;
  color: #6c757d;
  margin-top: 2px;
}

.cell-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  color: #495057;
}

.text-danger {
  color: #dc3545;
}

.empty-state {
  text-align: center;
  color: #999;
  padding: 40px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 14px;
}

.member-query-panel {
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.empty-hint,
.config-error-state {
  text-align: center;
  padding: 48px 20px;
  color: #6c757d;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.empty-hint i,
.config-error-state i {
  font-size: 44px;
  display: block;
  margin-bottom: 12px;
  color: #adb5bd;
}

.config-error-state p {
  margin-bottom: 16px;
  line-height: 1.6;
}

.raw-json {
  margin: 0;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.6;
  overflow-x: auto;
  max-height: 320px;
}
</style>
